import React, { useCallback, useEffect, useState } from "react";
import { Upload, Trash2, X, ScrollText, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y,m,d]=String(iso).split("-"); return y&&m&&d?`${d}-${m}-${y}`:iso; };

export function AdminLedger({ token, onUnauthorized }) {
  const [customers, setCustomers] = useState([]);
  const [selCust, setSelCust] = useState("");
  const [uploads, setUploads] = useState(null);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, new_preview, duplicate_preview }
  const [previewFile, setPreviewFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmPurge, setConfirmPurge] = useState(false);

  const loadCustomers = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return onUnauthorized?.();
    const list = await r.json();
    setCustomers(list);
    if (!selCust && list.length > 0) setSelCust(list[0].id);
  }, [token, onUnauthorized, selCust]);

  const loadCustomerData = useCallback(async () => {
    if (!selCust) return;
    const [u, s] = await Promise.all([
      fetch(`${API_BASE}/api/admin/ledger/uploads/${selCust}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      fetch(`${API_BASE}/api/admin/ledger/summary/${selCust}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    ]);
    setUploads(u); setSummary(s);
  }, [selCust, token]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadCustomerData(); }, [loadCustomerData]);

  const onFilePreview = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selCust) return;
    setBusy("preview");
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("customer_id", selCust);
      const r = await fetch(`${API_BASE}/api/admin/ledger/preview`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Preview failed");
      setPreview(data); setPreviewFile(file);
    } catch (err) { alert(err.message); }
    finally { setBusy(""); }
  };

  const commitImport = async () => {
    if (!previewFile || !selCust) return;
    setBusy("upload");
    try {
      const fd = new FormData(); fd.append("file", previewFile); fd.append("customer_id", selCust);
      const r = await fetch(`${API_BASE}/api/admin/ledger/upload`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      alert(`Imported ${data.inserted} new · Skipped ${data.skipped_duplicates} duplicates · Ignored ${data.skipped_totals} totals/blank rows`);
      setPreview(null); setPreviewFile(null);
      loadCustomerData();
    } catch (err) { alert(err.message); }
    finally { setBusy(""); }
  };

  const purge = async () => {
    if (!selCust) return;
    setBusy("purge");
    try {
      const r = await fetch(`${API_BASE}/api/admin/ledger/purge/${selCust}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      alert(`Deleted ${data.transactions_deleted} transactions.`);
      setConfirmPurge(false); loadCustomerData();
    } catch { alert("Purge failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="admin-ledger">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0a2a5e] flex items-center gap-2"><ScrollText className="h-5 w-5" /> Customer Ledger (Accounts)</h2>
          <p className="text-xs text-slate-500">Upload the customer's Tally export (PDF · XLSX · CSV). Duplicate voucher lines are skipped automatically.</p>
        </div>
        <select value={selCust} onChange={(e) => setSelCust(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white min-w-[240px]" data-testid="ledger-customer-select">
          <option value="">— Select customer —</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.code ? `(${c.code})` : ""}</option>)}
        </select>
      </div>

      {selCust && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Opening" value={summary.opening_balance} />
          <StatCard label="Total Sales" value={summary.total_sales} />
          <StatCard label="Total Receipts" value={summary.total_receipts} />
          <StatCard label="Closing" value={summary.closing_balance} bold />
        </div>
      )}

      {selCust && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-bold text-[#0a2a5e] mb-2">Upload Tally Ledger</h3>
          <p className="text-xs text-slate-500 mb-3">
            Choose your file format below. XML preserves every voucher detail from Tally and never mis-parses report totals.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3 relative">
              <div className="absolute -top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide">Recommended — Best Accuracy</div>
              <div className="font-bold text-emerald-900 text-sm mt-1">1. XML</div>
              <div className="text-xs text-emerald-700 mt-0.5">Structured export. Full voucher numbers, GUID dedup, exact ledger amounts.</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="font-semibold text-slate-800 text-sm">2. Excel (.xlsx)</div>
              <div className="text-xs text-slate-500 mt-0.5">Header row must include Date, Voucher No, Debit, Credit.</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="font-semibold text-slate-800 text-sm">3. CSV</div>
              <div className="text-xs text-slate-500 mt-0.5">Same column requirements as Excel.</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="font-semibold text-slate-800 text-sm">4. PDF <span className="text-slate-400">(fallback)</span></div>
              <div className="text-xs text-slate-500 mt-0.5">Text-based Tally PDFs only. Use only if XML/Excel unavailable.</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 rounded-full bg-[#0a2a5e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2a5e]/90 cursor-pointer" data-testid="ledger-upload-btn">
              <Upload className="h-4 w-4" /> {busy === "preview" ? "Parsing…" : "Choose File & Preview"}
              <input type="file" accept=".xml,.pdf,.xlsx,.xls,.csv" onChange={onFilePreview} className="hidden" />
            </label>
            <Button onClick={() => setConfirmPurge(true)} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1" /> Purge all ledger data
            </Button>
          </div>
        </div>
      )}

      {/* Uploads log */}
      {selCust && uploads && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="font-bold text-[#0a2a5e]">Upload History</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-semibold">Uploaded</th>
                <th className="px-4 py-3 font-semibold">Filename</th>
                <th className="px-4 py-3 font-semibold text-right">Rows</th>
                <th className="px-4 py-3 font-semibold text-right">Inserted</th>
                <th className="px-4 py-3 font-semibold text-right">Skipped</th>
                <th className="px-4 py-3 font-semibold text-right">Size (KB)</th>
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No uploads yet.</td></tr>}
              {uploads.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{(u.uploaded_at || "").replace("T"," ").slice(0,19)}</td>
                  <td className="px-4 py-3 font-semibold">{u.filename}</td>
                  <td className="px-4 py-3 text-right">{u.total_rows}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{u.inserted}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{u.skipped}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{Math.round((u.file_size || 0) / 1024)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#0a2a5e]">Validation Report — {preview.summary.filename}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-2 text-xs">
                  <StatMini label="Rows Read" value={preview.summary.total_rows_read || preview.summary.transactions_read} />
                  <StatMini label="Transactions" value={preview.summary.transactions_read} />
                  <StatMini label="New" value={preview.summary.new_rows} color="emerald" />
                  <StatMini label="Duplicates" value={preview.summary.duplicate_rows} color="amber" />
                  <StatMini label="Skipped (totals/blanks)" value={preview.summary.skipped_totals} color="slate" />
                  <StatMini label="Warnings" value={(preview.summary.warnings || []).length} color="orange" />
                  <StatMini label="Errors" value={(preview.summary.errors || []).length} color="red" />
                  <StatMini label="Opening Balance" value={`₹ ${inr(preview.summary.opening_balance || 0)}`} />
                </div>
              </div>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {preview.new_preview.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5 mb-2"><CheckCircle2 className="h-4 w-4" /> {preview.summary.new_rows} new rows will be imported (first {preview.new_preview.length} shown)</div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left font-semibold text-slate-500">Date</th>
                          <th className="px-2 py-2 text-left font-semibold text-slate-500">Particulars</th>
                          <th className="px-2 py-2 text-left font-semibold text-slate-500">Vch Type</th>
                          <th className="px-2 py-2 text-left font-semibold text-slate-500">Vch No</th>
                          <th className="px-2 py-2 text-right font-semibold text-slate-500">Debit</th>
                          <th className="px-2 py-2 text-right font-semibold text-slate-500">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.new_preview.map((r, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.txn_date)}</td>
                            <td className="px-2 py-1.5">
                              {r.direction && <span className="text-slate-500 mr-1">{r.direction}</span>}
                              <span className="font-medium">{r.ledger_name || r.particulars || "—"}</span>
                            </td>
                            <td className="px-2 py-1.5">{r.voucher_type || "—"}</td>
                            <td className="px-2 py-1.5 font-mono">{r.voucher_number || "—"}</td>
                            <td className="px-2 py-1.5 text-right">{r.debit ? inr(r.debit) : ""}</td>
                            <td className="px-2 py-1.5 text-right">{r.credit ? inr(r.credit) : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {preview.duplicate_preview.length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-amber-700 flex items-center gap-1.5 mb-2"><AlertCircle className="h-4 w-4" /> {preview.summary.duplicate_rows} duplicate rows will be skipped</div>
                  <div className="border border-amber-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {preview.duplicate_preview.map((r, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.txn_date)}</td>
                            <td className="px-2 py-1.5">{r.particulars}</td>
                            <td className="px-2 py-1.5 font-mono">{r.voucher_number}</td>
                            <td className="px-2 py-1.5 text-right">Dr {inr(r.debit)} / Cr {inr(r.credit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {preview.new_preview.length === 0 && preview.duplicate_preview.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">No transactions were detected in this file. Check that the header row includes Date, Voucher No, Debit and Credit columns.</div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
              <Button variant="outline" onClick={() => setPreview(null)} className="rounded-full">Cancel</Button>
              <Button onClick={commitImport} disabled={busy === "upload" || preview.summary.new_rows === 0}
                data-testid="ledger-commit-btn"
                className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white disabled:opacity-40">
                {busy === "upload" ? "Importing…" : `Import ${preview.summary.new_rows} rows`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {confirmPurge && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConfirmPurge(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-red-700 mb-2">Purge Ledger?</h3>
            <p className="text-sm text-slate-600 mb-4">This will permanently delete <b>all</b> ledger transactions and opening balance for this customer. This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmPurge(false)} className="rounded-full">Cancel</Button>
              <Button onClick={purge} className="rounded-full bg-red-600 hover:bg-red-700 text-white">Purge</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const StatMini = ({ label, value, color = "slate" }) => {
  const colors = {
    slate: "text-slate-800", emerald: "text-emerald-700", amber: "text-amber-700",
    red: "text-red-700", orange: "text-orange-700",
  };
  return (
    <div>
      <div className="text-slate-400 uppercase tracking-wider text-[10px]">{label}</div>
      <div className={`font-bold ${colors[color] || colors.slate}`}>{value}</div>
    </div>
  );
};

const StatCard = ({ label, value, bold }) => (
  <div className={`bg-white rounded-2xl border border-slate-100 p-4 shadow-sm ${bold ? "ring-2 ring-[#0a2a5e]/10" : ""}`}>
    <div className="text-xs text-slate-500 font-semibold">{label}</div>
    <div className={`text-lg font-black ${bold ? "text-[#0a2a5e]" : "text-slate-800"}`}>₹ {inr(value)}</div>
  </div>
);
